// components/ReportsDashboard.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  UsersIcon,
  DocumentTextIcon,
  CalendarIcon,
  CheckCircleIcon,
  BuildingStorefrontIcon,
  BuildingOfficeIcon,
  BuildingLibraryIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  TableCellsIcon
} from '@heroicons/react/24/solid';
import { reportsAPI } from '../../services/api';
import { downloadBlobResponse, extensionForFormat } from '../../utils/downloadFile';

/* UI-only port. All state, API calls, filter logic, select-all/clear,
   report generation, Excel/PDF export and the three report tabs are
   byte-for-byte the same behaviour as before.

   Presentation changes:
     - the blue gradient hero + illustration panel becomes a compact
       control-panel strip (breadcrumb, company picker, Generate, Export)
     - filter lists become Odoo "many2many" boxes: hairline, tight rows,
       a count in the header, Select all / Clear as quiet links
     - report tables lose the gradient header and 24px padding for
       hairline heads and 8px rows
     - participation / rating pills keep their traffic-light semantics
       but read from the badge tokens so they work in dark mode
*/

const ReportsDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [filters, setFilters] = useState({
    surveyIds: [],
    branchIds: [],
    departmentIds: [],
    divisionIds: [],
    fromDate: null,
    toDate: null
  });
  const [filterOptions, setFilterOptions] = useState({
    surveys: [],
    branches: [],
    departments: [],
    divisions: []
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);
  const [exportFormat, setExportFormat] = useState(null); // 'excel' | 'pdf' | null while exporting
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadFilterOptions();
    }
  }, [selectedCompanyId]);

  const loadCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const response = await reportsAPI.getCompanies();
      setCompanies(response.data);
      // A CompanyAdmin's token now only ever returns their own company here,
      // so auto-select it instead of making them pick from a one-item dropdown.
      // SuperAdmin still sees (and picks from) the full list.
      if (response.data?.length === 1) {
        setSelectedCompanyId(String(response.data[0].id));
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      setFiltersLoading(true);
      const response = await reportsAPI.getFilters(selectedCompanyId);
      setFilterOptions(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load filter options');
      console.error('Error loading filters:', err);
    } finally {
      setFiltersLoading(false);
    }
  };

  const generateReport = async () => {
    if (!selectedCompanyId) {
      setError('Please select a company first');
      return;
    }

    try {
      setLoading(true);
      const requestData = {
        companyId: selectedCompanyId,
        ...filters
      };

      const response = await reportsAPI.generateReport(requestData);
      setReportData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to generate report');
      console.error('Error generating report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (!selectedCompanyId) {
      setError('Please select a company first');
      return;
    }

    setExportMenuOpen(false);
    try {
      setExportFormat(format);
      const requestData = {
        companyId: selectedCompanyId,
        ...filters
      };
      const response = await reportsAPI.exportReport(requestData, format);
      downloadBlobResponse(response, `all-reports.${extensionForFormat(format)}`);
      setError(null);
    } catch (err) {
      setError('Failed to export report');
      console.error('Error exporting report:', err);
    } finally {
      setExportFormat(null);
    }
  };

  const handleCompanyChange = (companyId) => {
    setSelectedCompanyId(companyId);
    setFilters({
      surveyIds: [],
      branchIds: [],
      departmentIds: [],
      divisionIds: [],
      fromDate: null,
      toDate: null
    });
    setReportData(null);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSelectAll = (type) => {
    const allIds = filterOptions[type]?.map(item => item.id) || [];
    handleFilterChange(`${type}Ids`, allIds);
  };

  const handleDeselectAll = (type) => {
    handleFilterChange(`${type}Ids`, []);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Traffic-light tones, now expressed as badge tokens so both themes work.
  const participationTone = (rate) => {
    if (rate >= 80) return 'green';
    if (rate >= 60) return 'amber';
    if (rate >= 40) return 'red';
    return 'grey';
  };

  const ratingTone = (rating) => {
    if (rating >= 4) return 'green';
    if (rating >= 3) return 'amber';
    if (rating >= 2) return 'red';
    return 'grey';
  };

  const pill = (tone) => ({
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background: `var(--b-${tone}-bg)`,
    color: `var(--b-${tone}-fg)`
  });

  const ghostBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    height: 28,
    padding: '0 10px',
    background: 'var(--surface)',
    border: '1px solid var(--line-3)',
    borderRadius: 3,
    color: 'var(--body-2)',
    fontSize: 12.5,
    cursor: 'pointer'
  };

  const th = {
    textAlign: 'left',
    padding: '7px 10px',
    fontSize: 11.5,
    fontWeight: 600,
    color: 'var(--muted)',
    borderBottom: '1px solid var(--line)',
    whiteSpace: 'nowrap'
  };

  const td = {
    padding: '8px 10px',
    fontSize: 12.5,
    color: 'var(--body-2)',
    borderBottom: '1px solid var(--line-soft)',
    whiteSpace: 'nowrap'
  };

  const fieldInput = {
    width: '100%',
    height: 30,
    padding: '0 8px',
    background: 'var(--surface)',
    border: '1px solid var(--line-3)',
    borderRadius: 3,
    color: 'var(--body)',
    fontSize: 12.5,
    outline: 'none'
  };

  /* One Odoo-style multi-select box. Same checkbox semantics as before —
     only the chrome and density changed. */
  const FilterBox = ({ label, type, items, idsKey }) => {
    const ids = filters[idsKey];
    return (
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <label className="text-[12.5px] font-semibold" style={{ color: 'var(--body-2)' }}>
            {label}
            <span className="ml-1.5 font-normal" style={{ color: 'var(--faint)' }}>
              {ids.length > 0 ? `${ids.length} of ${items.length}` : `${items.length}`}
            </span>
          </label>
          <div className="flex items-center gap-2 text-[11.5px]">
            <button
              onClick={() => handleSelectAll(type)}
              style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--accent)' }}
            >
              Select all
            </button>
            <span style={{ color: 'var(--dim)' }}>·</span>
            <button
              onClick={() => handleDeselectAll(type)}
              style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--muted)' }}
            >
              Clear
            </button>
          </div>
        </div>
        <div
          className="max-h-40 overflow-y-auto rounded-[3px]"
          style={{ border: '1px solid var(--line-3)', background: 'var(--surface)' }}
        >
          {items.length === 0 ? (
            <div className="px-2.5 py-3 text-[12px]" style={{ color: 'var(--faint)' }}>
              Nothing available
            </div>
          ) : (
            items.map(item => {
              const checked = ids.includes(item.id);
              return (
                <label
                  key={item.id}
                  htmlFor={`${type}-${item.id}`}
                  className="flex cursor-pointer items-center gap-2 px-2.5 py-[5px] text-[12.5px] transition-colors"
                  style={{
                    color: checked ? 'var(--ink)' : 'var(--body-2)',
                    fontWeight: checked ? 600 : 400,
                    background: checked ? 'var(--row-sel)' : 'transparent',
                    borderBottom: '1px solid var(--line-soft)'
                  }}
                >
                  <input
                    type="checkbox"
                    id={`${type}-${item.id}`}
                    checked={checked}
                    onChange={(e) => {
                      const newIds = e.target.checked
                        ? [...ids, item.id]
                        : ids.filter(id => id !== item.id);
                      handleFilterChange(idsKey, newIds);
                    }}
                    className="h-[13px] w-[13px] flex-shrink-0 cursor-pointer"
                    style={{ accentColor: 'var(--accent)' }}
                  />
                  <span className="truncate">{item.name}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderTable = (data, type) => {
    if (!data || data.length === 0) {
      return (
        <div className="py-10 text-center text-[12.5px]" style={{ color: 'var(--faint)' }}>
          No data available for {type} reports
        </div>
      );
    }

    const categories = reportData?.categories || [];

    return (
      <div className="overflow-x-auto rounded-[4px]" style={{ border: '1px solid var(--line)' }}>
        <table className="min-w-full border-collapse">
          <thead>
            <tr style={{ background: 'var(--surface-2)' }}>
              <th style={{ ...th, width: 50 }}>S#</th>
              <th style={{ ...th, minWidth: 180 }}>Survey</th>
              <th style={{ ...th, minWidth: 150 }}>
                {type === 'branch' ? 'Branch' : type === 'department' ? 'Department' : 'Division'}
              </th>
              <th style={th}>Participation</th>
              {categories.map(category => (
                <th key={category} style={{ ...th, textAlign: 'center' }}>{category}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr
                key={index}
                style={{ background: 'var(--surface)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--row-sel)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--surface)')}
              >
                <td style={{ ...td, color: 'var(--faint)' }}>{row.serialNumber}</td>
                <td style={{ ...td, color: 'var(--ink)', fontWeight: 600 }}>{row.surveyName}</td>
                <td style={td}>
                  {type === 'branch' ? row.branchName :
                   type === 'department' ? row.departmentName : row.divisionName}
                </td>
                <td style={td}>
                  <span style={pill(participationTone(row.participationRate))}>
                    {row.participationRate.toFixed(1)}%
                  </span>
                </td>
                {categories.map(category => (
                  <td key={category} style={{ ...td, textAlign: 'center' }}>
                    <span style={pill(ratingTone(row.categoryAverages[category] || 0))}>
                      {(row.categoryAverages[category] || 0).toFixed(1)}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (companiesLoading) {
    return (
      <div className="flex h-64 items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div
          className="h-8 w-8 animate-spin rounded-full border-b-2"
          style={{ borderColor: 'var(--accent)' }}
        />
      </div>
    );
  }

  const selectedCompany = companies.find(c => String(c.id) === String(selectedCompanyId));

  return (
    <div className="min-h-full" style={{ background: 'var(--bg)' }}>
      {/* ══ CONTROL PANEL ══ */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-[14px] pb-2 pt-[9px]">
          <div className="flex min-w-0 items-baseline gap-[7px]">
            <span className="hidden text-[13px] sm:block" style={{ color: 'var(--accent)' }}>Reports</span>
            <span className="hidden sm:block" style={{ color: 'var(--dim)' }}>/</span>
            <h1
              className="m-0 truncate text-[18px] font-semibold tracking-[-.2px]"
              style={{ color: 'var(--ink)' }}
            >
              All Reports
            </h1>
          </div>

          <div className="flex items-center gap-[6px]">
            <button
              onClick={generateReport}
              disabled={loading || filtersLoading || !selectedCompanyId}
              className="inline-flex h-7 items-center gap-[5px] rounded-[3px] px-[13px] text-[12.5px] font-semibold disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: 'var(--accent)', color: 'var(--on-accent)', border: 0, cursor: 'pointer' }}
            >
              {loading ? (
                <>
                  <span
                    className="h-3.5 w-3.5 animate-spin rounded-full border-b-2"
                    style={{ borderColor: 'var(--on-accent)' }}
                  />
                  Generating…
                </>
              ) : (
                <>
                  <ChartBarIcon className="h-[13px] w-[13px]" />
                  Generate
                </>
              )}
            </button>

            <div className="relative">
              <button
                onClick={() => setExportMenuOpen((open) => !open)}
                disabled={exportFormat !== null || !reportData}
                style={{ ...ghostBtn, opacity: exportFormat !== null || !reportData ? 0.5 : 1 }}
              >
                {exportFormat ? (
                  <span
                    className="h-3.5 w-3.5 animate-spin rounded-full border-b-2"
                    style={{ borderColor: 'var(--accent)' }}
                  />
                ) : (
                  <ArrowDownTrayIcon className="h-[13px] w-[13px]" />
                )}
                {exportFormat ? `Exporting ${exportFormat.toUpperCase()}…` : 'Export'}
                <ChevronDownIcon className="h-[11px] w-[11px]" />
              </button>
              {exportMenuOpen && (
                <div
                  className="absolute right-0 z-30 mt-1 w-44 overflow-hidden rounded-[4px]"
                  style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow)' }}
                >
                  <button
                    onClick={() => handleExport('excel')}
                    className="flex w-full items-center gap-2 px-3 py-[7px] text-left text-[12.5px]"
                    style={{ color: 'var(--body)', background: 'transparent', border: 0, cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <TableCellsIcon className="h-4 w-4" style={{ color: 'var(--b-green-fg)' }} />
                    Export to Excel
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="flex w-full items-center gap-2 px-3 py-[7px] text-left text-[12.5px]"
                    style={{ color: 'var(--body)', background: 'transparent', border: 0, cursor: 'pointer', borderTop: '1px solid var(--line-soft)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <DocumentTextIcon className="h-4 w-4" style={{ color: 'var(--b-red-fg)' }} />
                    Export to PDF
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="min-w-[60px] flex-1" />

          {/* Company scope — a facet, not a hero panel */}
          <div className="flex items-center gap-2">
            <span className="text-[11.5px] font-semibold uppercase tracking-[.07em]" style={{ color: 'var(--faint)' }}>
              Company
            </span>
            {companies.length === 1 ? (
              <span
                className="inline-flex h-7 items-center gap-1.5 rounded-[3px] px-2.5 text-[12.5px] font-semibold"
                style={{ background: 'var(--tint)', color: 'var(--tint-ink)' }}
              >
                <BuildingOfficeIcon className="h-[13px] w-[13px]" />
                {companies[0].name}
              </span>
            ) : (
              <select
                value={selectedCompanyId}
                onChange={(e) => handleCompanyChange(e.target.value)}
                className="h-7 min-w-[180px] rounded-[3px] px-2 text-[12.5px] outline-none"
                style={{ background: 'var(--surface)', border: '1px solid var(--line-3)', color: 'var(--body)' }}
              >
                <option value="">Choose a company…</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-[14px] p-[14px]">
        {/* Error Display */}
        {error && (
          <div
            className="flex items-center gap-2 rounded-[4px] px-3 py-2.5 text-[12.5px]"
            style={{ background: 'var(--b-red-bg)', border: '1px solid var(--line)', color: 'var(--b-red-fg)' }}
          >
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {/* Filters */}
        {selectedCompanyId && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-[4px]"
            style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}
          >
            <div
              className="flex items-center justify-between px-[14px] py-2.5"
              style={{ borderBottom: '1px solid var(--line-2)' }}
            >
              <span className="text-[13.5px] font-semibold" style={{ color: 'var(--ink)' }}>Filters</span>
              {selectedCompany && (
                <span className="flex items-center gap-1.5 text-[11.5px]" style={{ color: 'var(--faint)' }}>
                  <CheckCircleIcon className="h-[13px] w-[13px]" style={{ color: 'var(--accent)' }} />
                  Scoped to {selectedCompany.name}
                </span>
              )}
            </div>

            {filtersLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div
                  className="h-6 w-6 animate-spin rounded-full border-b-2"
                  style={{ borderColor: 'var(--accent)' }}
                />
                <span className="ml-3 text-[12.5px]" style={{ color: 'var(--muted)' }}>
                  Loading filter options…
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 p-[14px] lg:grid-cols-2">
                <FilterBox label="Surveys" type="surveys" items={filterOptions.surveys} idsKey="surveyIds" />
                <FilterBox label="Branches" type="branches" items={filterOptions.branches} idsKey="branchIds" />
                <FilterBox label="Departments" type="departments" items={filterOptions.departments} idsKey="departmentIds" />
                <FilterBox label="Divisions" type="divisions" items={filterOptions.divisions} idsKey="divisionIds" />

                {/* Date Range */}
                <div className="lg:col-span-2">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="grid grid-cols-[minmax(0,90px)_1fr] items-center gap-2.5">
                      <label className="flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: 'var(--body-2)' }}>
                        <CalendarIcon className="h-[13px] w-[13px]" style={{ color: 'var(--faint)' }} />
                        From
                      </label>
                      <input
                        type="date"
                        value={filters.fromDate || ''}
                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                        style={fieldInput}
                      />
                    </div>
                    <div className="grid grid-cols-[minmax(0,90px)_1fr] items-center gap-2.5">
                      <label className="flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: 'var(--body-2)' }}>
                        <CalendarIcon className="h-[13px] w-[13px]" style={{ color: 'var(--faint)' }} />
                        To
                      </label>
                      <input
                        type="date"
                        value={filters.toDate || ''}
                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                        style={fieldInput}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Reports */}
        {reportData && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden rounded-[4px]"
            style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}
          >
            {/* Tabs — Odoo notebook */}
            <div className="flex overflow-x-auto px-[14px]" style={{ borderBottom: '1px solid var(--line-2)' }}>
              {[
                { name: 'Branch', icon: BuildingStorefrontIcon, count: reportData.branchReports?.length },
                { name: 'Department', icon: BuildingOfficeIcon, count: reportData.departmentReports?.length },
                { name: 'Division', icon: BuildingLibraryIcon, count: reportData.divisionReports?.length }
              ].map((tab, index) => {
                const active = activeTab === index;
                return (
                  <button
                    key={tab.name}
                    onClick={() => setActiveTab(index)}
                    className="flex items-center gap-2 whitespace-nowrap px-3.5 py-2.5 text-[12.5px]"
                    style={{
                      background: 'transparent',
                      border: 0,
                      borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
                      color: active ? 'var(--tint-ink)' : 'var(--muted)',
                      fontWeight: active ? 600 : 500,
                      cursor: 'pointer'
                    }}
                  >
                    <tab.icon className="h-[14px] w-[14px]" />
                    {tab.name}
                    {tab.count > 0 && (
                      <span
                        className="rounded-[9px] px-[7px] text-[11px] font-semibold"
                        style={
                          active
                            ? { background: 'var(--tint)', color: 'var(--tint-ink)' }
                            : { background: 'var(--track)', color: 'var(--muted)' }
                        }
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="p-[14px]">
              {activeTab === 0 && renderTable(reportData.branchReports, 'branch')}
              {activeTab === 1 && renderTable(reportData.departmentReports, 'department')}
              {activeTab === 2 && renderTable(reportData.divisionReports, 'division')}
            </div>
          </motion.div>
        )}

        {/* No Company Selected State */}
        {!selectedCompanyId && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-[4px] px-6 py-14 text-center"
            style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}
          >
            <BuildingStorefrontIcon className="mx-auto mb-3 h-10 w-10" style={{ color: 'var(--dim)' }} />
            <h3 className="mb-1 text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>
              No company selected
            </h3>
            <p className="mx-auto max-w-md text-[12.5px]" style={{ color: 'var(--faint)' }}>
              Pick a company in the control panel above to load its surveys and organisational structure.
            </p>
          </motion.div>
        )}

        {/* Nothing generated yet */}
        {selectedCompanyId && !reportData && !loading && (
          <div
            className="rounded-[4px] px-6 py-14 text-center"
            style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}
          >
            <ChartBarIcon className="mx-auto mb-3 h-10 w-10" style={{ color: 'var(--dim)' }} />
            <h3 className="mb-1 text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>
              No report generated
            </h3>
            <p className="mx-auto max-w-md text-[12.5px]" style={{ color: 'var(--faint)' }}>
              Choose your filters, then hit Generate.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsDashboard;
